import { CustomBottomBar, CustomPageTitle, FileItem, MinimalStepper } from '@components';
import { Button, Container, CustomIcon, Icon, Typography, playIcon } from '@design-system';
import { useVisibilityRerender } from '@hooks';
import React, { useCallback, useRef } from 'react';
import { FaGraduationCap } from 'react-icons/fa';
import { FaRegFilePdf } from 'react-icons/fa6';
import { GrDownload, GrFormCheckmark } from 'react-icons/gr';
import { MdKeyboardArrowRight } from 'react-icons/md';
import ReactPlayer from 'react-player';
import { useNavigate } from 'react-router-dom';
import './FormationTalkFilesPage.scss';
import { FormationTalkFilesSkeleton } from './FormationTalkFilesSkeleton';
import { useFormationTalkFilesPageVM } from './useFormationTalkFilesPageVM';

export const FormationTalkFilesPage: React.FC = () => {
    const {
        categoryId,
        formationId,
        pageTitle,
        files,
        videos,
        isLoading,
        acknowledgeFile,
        acknowledgeVideo,
        areFilesAcknowledged,
        isEveryAssetAcknowledged
    } = useFormationTalkFilesPageVM();

    const navigate = useNavigate();
    const clickedVideoIdRef = useRef<number | null>(null);

    const handleVisibilityReturn = useCallback(() => {
        if (clickedVideoIdRef.current !== null) {
            acknowledgeVideo(clickedVideoIdRef.current);
            clickedVideoIdRef.current = null;
        }
    }, [acknowledgeVideo]);

    const videoRerenderKey = useVisibilityRerender(handleVisibilityReturn);

    if (isLoading) {
        return <FormationTalkFilesSkeleton />;
    }

    return (
        <div className="formation-talk-files-page">
            {pageTitle && (
                <CustomPageTitle>
                    <Typography intrinsic="h3" iconColor="blue" icon={<FaGraduationCap />} iconFilled iconSize={20}>
                        {pageTitle}
                    </Typography>
                </CustomPageTitle>
            )}

            <Container className="formation-page-header" radius="none" column>
                <Typography intrinsic="h2" className="mb-large-spacing">
                    Les instructions
                </Typography>
                <MinimalStepper
                    steps={[
                        { label: 'Les instructions', status: isEveryAssetAcknowledged ? 'completed' : 'inProgress' },
                        { label: 'Le quizz', status: 'pending' },
                        { label: 'Emargement', status: 'pending' }
                    ]}
                />
            </Container>

            {isLoading ? (
                <FormationTalkFilesSkeleton />
            ) : (
                <>
                    <Container>
                        {videos.map((video, index) => (
                            <Container key={video.id} bordered="grey" className="video-container">
                                <Typography intrinsic="h4" defaultSpacing>
                                    Vidéo {videos.length > 1 ? `(${index + 1})` : ''}
                                </Typography>
                                <ReactPlayer
                                    key={`${video.id}-${videoRerenderKey}`}
                                    url={video.url}
                                    className="video-player"
                                    controls
                                    light
                                    playing={true}
                                    onClickPreview={() => {
                                        clickedVideoIdRef.current = video.id;
                                    }}
                                    onPlay={() => {
                                        setTimeout(() => {
                                            acknowledgeVideo(video.id);
                                            clickedVideoIdRef.current = null;
                                        }, 5000);
                                    }}
                                    playIcon={<CustomIcon src={playIcon} alt="play" />}
                                    config={{
                                        youtube: {
                                            playerVars: {
                                                playsinline: 1
                                            }
                                        }
                                    }}
                                />
                                <Typography intrinsic="p" color="marine" className="pl-small-spacing mt-small-medium-spacing">
                                    {video.name}
                                </Typography>
                                {video.consulted && <Icon icon={<GrFormCheckmark />} size={28} color="green" className="video-check" />}
                            </Container>
                        ))}

                        <Container bordered="grey" className="files-container">
                            <Typography intrinsic="h4" defaultSpacing>
                                Documentation
                            </Typography>
                            {files.map(({ id, name, path, consulted }) => (
                                <a href={path} target="_blank" rel="noopener noreferrer" key={id}>
                                    <FileItem
                                        title={name}
                                        icon={<Icon icon={<FaRegFilePdf />} size={20} />}
                                        onClick={async () => {
                                            acknowledgeFile(id);
                                        }}
                                        customRightIcon={consulted ? null : <Icon icon={<GrDownload />} size={20} />}
                                        className={`${consulted ? 'bg-grey4' : ''}`}
                                    />
                                </a>
                            ))}
                            {areFilesAcknowledged && <Icon icon={<GrFormCheckmark />} size={28} color="green" className="file-check" />}
                        </Container>
                    </Container>
                </>
            )}

            <CustomBottomBar>
                <Button
                    color="marine"
                    size="fullwidth"
                    icon={<MdKeyboardArrowRight />}
                    iconPosition="forced-right"
                    iconColor="white"
                    iconSize={30}
                    disabled={!isEveryAssetAcknowledged}
                    onClick={() => {
                        navigate(`/sante-securite/formations/list/${categoryId}/questions/${formationId}`);
                    }}
                >
                    <Typography intrinsic="p">Continuer vers le quiz</Typography>
                </Button>
            </CustomBottomBar>
        </div>
    );
};
